/**
 * Module F-Lite — typed engine API client with session-token auth.
 *
 * Reads VITE_ENGINE_URL at build time (the engine origin, e.g.
 *   https://api.siteskick.com). The session token is loaded from
 * localStorage at module-init AND on every request (so logout/login in
 * another tab is honored on the next call). If no token is present,
 * requests fire without Authorization — useful for /api/auth/login (which
 * is public anyway).
 *
 * On 401 from the engine, the client clears the stored token and emits a
 * 'sitekit:session-expired' window event so the UI can redirect to /login.
 */

const ENGINE_URL = (import.meta.env.VITE_ENGINE_URL ?? '').replace(/\/+$/, '');

if (!ENGINE_URL) {
  // eslint-disable-next-line no-console
  console.warn('VITE_ENGINE_URL is empty — API calls will fail. Set it in .env.local.');
}

const TOKEN_KEY = 'sitekit:session-token';

export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export function setToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(TOKEN_KEY);
    else localStorage.setItem(TOKEN_KEY, token);
  } catch { /* private-mode browser w/o storage; ignore */ }
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message?: string) {
    super(message ?? `Engine API ${status}`);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${ENGINE_URL}${path.startsWith('/') ? path : '/' + path}`;
  const token = getToken();
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 401) {
    // Session expired (or never had one). Clear + notify the app.
    setToken(null);
    window.dispatchEvent(new Event('sitekit:session-expired'));
  }
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { body = await res.text(); }
    const message =
      typeof body === 'object' && body && 'errorMessage' in body
        ? String((body as { errorMessage: string }).errorMessage)
        : typeof body === 'object' && body && 'error' in body
          ? String((body as { error: string }).error)
          : `HTTP ${res.status}`;
    throw new ApiError(res.status, body, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  // --- auth ---
  login: (email: string, password: string) =>
    request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () =>
    request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  me: () => request<MeResponse>('/api/auth/me'),

  // --- projects (RLS-filtered server-side) ---
  listProjects: () => request<{ projects: ProjectSummary[] }>('/api/projects'),
  getProjectState: (projectId: string) =>
    request<ProjectState>(`/api/projects/${projectId}/state`),
  createProject: (body: CreateProjectBody) =>
    request<CreateProjectResponse>('/api/projects', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  approveSkeleton: (projectId: string) =>
    request<{ ok: true; finalSkeletonVersion: number }>(
      `/api/projects/${projectId}/skeleton/approve`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  startBuildRun: (projectId: string) =>
    request<{ buildRunId: string; jobId: string }>(
      `/api/projects/${projectId}/buildruns`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  acceptProject: (projectId: string) =>
    request<{ ok: true; acceptedAt?: string; alreadyAccepted?: boolean }>(
      `/api/projects/${projectId}/accept`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  // --- deploy ---
  deploy: (projectId: string, snapshotId: string, domainConfigId: string) =>
    request<{ jobId: string }>(`/api/projects/${projectId}/deployments`, {
      method: 'POST',
      body: JSON.stringify({ snapshotId, domainConfigId, policy: 'pinned_snapshot' }),
    }),
  // --- promote to production ---
  promoteToProduction: (
    projectId: string,
    hostname: string,
    aliases: string[] = [],
  ) =>
    request<PromoteResponse>(
      `/api/admin/projects/${projectId}/promote-to-production`,
      {
        method: 'POST',
        body: JSON.stringify({ hostname, aliases }),
      },
    ),
  // --- validation ---
  latestValidation: (projectId: string) =>
    request<{ report: ValidationReport }>(`/api/projects/${projectId}/validation/latest`),
  // --- snapshot download (binary) ---
  // Uses fetch+blob because <a href> can't carry Authorization.
  downloadSnapshot: async (projectId: string, snapshotId: string): Promise<void> => {
    const url = `${ENGINE_URL}/api/projects/${projectId}/snapshots/${snapshotId}/download`;
    const token = getToken();
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      throw new ApiError(res.status, await res.text().catch(() => null), `download failed: ${res.status}`);
    }
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') ?? '';
    const filenameMatch = cd.match(/filename="?([^";]+)"?/i);
    const filename = filenameMatch?.[1] ?? `snapshot-${snapshotId}.tar.gz`;
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  },
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LoginResponse {
  token: string;
  expiresAt: string;
  user: { id: string; email: string; displayName?: string };
}

export interface MeResponse {
  user: { id: string; email: string; displayName?: string; lastLoginAt?: string } | null;
  bearerAuth?: boolean;
}

export interface ProjectSummary {
  id: string;
  brandId: string;
  kitId: string;
  liveDeploymentId?: string;
  currentBuildRunId?: string;
  ownerUserId?: string;
  acceptedAt?: string;
  autoAccept: boolean;
  displayName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectState {
  project: ProjectSummary;
  buildRun?: BuildRun;
  lockHolder: string | null;
  lockExpiresAt: string | null;
}

export interface BuildRun {
  id: string;
  projectId: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'halted_manual_edits' | 'awaiting_plan_decision' | 'paused_resumable';
  lastCompletedPhase?: string;
  snapshotId?: string;
  totalCostUsd?: number;
  startedAt: string;
  completedAt?: string;
  errorCode?: string;
  errorMessage?: string;
}

export interface CreateProjectBody {
  brand: unknown;
  kitId: string;
  logo?: { fileBase64: string; format: string };
}

export interface CreateProjectResponse {
  projectId: string;
  brandId: string;
  kitId: string;
  domainConfigId: string;
  stagingHostname: string;
  warnings?: Array<{ code: string; message: string }>;
}

export interface PromoteResponse {
  domainConfigId: string;
  oldRole: string;
  newRole: string;
  oldCanonicalHost: string;
  newCanonicalHost: string;
  aliases: string[];
  buildRunId: string;
  jobId: string;
  estimatedDurationSec: number;
  nextSteps: string[];
}

export interface ValidationReport {
  id: string;
  outcome: 'pass' | 'fail';
  failureSubtype?: string;
  data: {
    pages: Record<string, { outcome: 'pass' | 'fail'; issues: ValidationIssue[] }>;
    global: Record<string, ValidationIssue[]>;
  };
}

export interface ValidationIssue {
  ruleId: string;
  severity: 'blocking' | 'warning';
  message: string;
}
